import { useEffect, Fragment, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  getCurrentUser,
  isOrganizer,
  isGoverner,
} from "@neumetric/blueprint-lib/store";
import {
  Col,
  usePub,
  NMWaitIndicator,
} from "@neumetric/blueprint-lib/components";
import { NMObjectHeader } from "@neumetric/blueprint-lib/flows";
import { InfoSection } from "./Info";
import { RiskAcceptanceSection } from "./Acceptance";
import { MitigationSection } from "./Mitigation";
import { RiskReport } from "./Report";
import {
  RiskHistoryPopup,
  RiskReferencesPopup,
  RiskConfirmationPopup,
} from "./Dialogs";
import {
  riskActions,
  getCurrentRisk,
  isRiskTolerate,
  isRiskArchived,
  isRiskAccepted,
  isRiskCompleted,
  getRiskStatusList,
  getSeverityList,
  getDecisionList,
} from "../../store";

const MenuItems = [
  {
    icon: "SUBMIT",
    shouldCloseOnClick: true,
    id: "report",
    label: "Risk Report",
    variant: "CONTRAST",
  },
  {
    icon: "RECOVER",
    shouldCloseOnClick: true,
    id: "history",
    label: "Risk History",
    variant: "CONTRAST",
  },
  {
    icon: "BOOK",
    shouldCloseOnClick: true,
    id: "reference",
    label: "References",
    variant: "CONTRAST",
  },
  {
    id: "archive",
    label: "Archive",
    icon: "OPEN_FOLDER",
    variant: "CAUTION",
    shouldCloseOnClick: true,
  },
  {
    id: "unarchive",
    label: "Unarchive",
    icon: "OPEN_FOLDER",
    variant: "CAUTION",
    shouldCloseOnClick: true,
  },
];

export const RiskDetailsScreen = () => {
  const location = useLocation();
  const _headerRef = useRef(null);
  const _infoRef = useRef(null);
  const _acceptanceRef = useRef(null);
  const dispatch = useDispatch();
  const publish = usePub();
  const navigate = useNavigate();
  const { riskId } = useParams();
  const currentUser = useSelector(getCurrentUser);
  const riskStatusList = useSelector(getRiskStatusList);
  const isOrganizerUser = isOrganizer(currentUser);
  const isGovernerUser = isGoverner(currentUser);
  const currentRisk = useSelector(getCurrentRisk);
  const isRiskTolerated = useSelector(isRiskTolerate);
  const isRiskArchive = useSelector(isRiskArchived);
  const isRiskAccept = useSelector(isRiskAccepted);
  const decisionList = useSelector(getDecisionList);
  const severityList = useSelector(getSeverityList);

  const [state, setState] = useState({
    selectedMenu: null,
    dialogConfig: null,
  });

  useEffect(() => {
    if (!currentRisk) return;

    const sp = new URLSearchParams(location.search);

    // View report
    const view = (sp.get("view") || "").toLowerCase();
    const isViewReportLink = view === "report";

    // Mark as reviewed
    const action = (sp.get("action") || "").toLowerCase();
    const attribute = (sp.get("attribute") || "").toLowerCase();
    const value = (sp.get("value") || "").toLowerCase();

    const isMarkReviewedLink =
      action === "update" && attribute === "status" && value === "reviewed";

    // Accept risk dialog from email
    const isAcceptRiskLink = action === "accept";

    if (!isViewReportLink && !isMarkReviewedLink && !isAcceptRiskLink) return;

    // prevent retrigger on refresh/back
    navigate(location.pathname, { replace: true });

    if (isViewReportLink) {
      setState((prev) => ({ ...prev, selectedMenu: "report" }));
      return;
    }

    if (isMarkReviewedLink) {
      handleMarkAsReviewed();
      return;
    }

    // open the  Accept dialog
    if (isAcceptRiskLink) {
      handleAccept();
    }
  }, [currentRisk, location.search]);

  useEffect(() => {
    dispatch(riskActions.fetchRisk({ riskId: riskId }));
    dispatch(riskActions.fetchReferences());
  }, [riskId]);

  const handleEdit = () => {
    publish("RISK_EDIT_MODE", true);
  };

  const updateState = (newState) => {
    setState({ ...state, ...newState });
  };

  const handleGoBack = () => {
    navigate(`..`);
  };

  const handleMenuClick = (item) => {
    if (item.id === "archive") {
      return handleArchive();
    } else if (item.id === "unarchive") {
      return handleUnarchive();
    }
    updateState({ selectedMenu: item.id });
  };

  const handleArchive = () => {
    const onComplete = () => {
      updateState({ selectedMenu: null, dialogConfig: null });
    };
    const onConfirm = (_, __, callback) => {
      const comment = `The Risk is archived by <span class="fw-700">${currentUser.name}</span>.`;
      const payload = {
        risk: { status: "archived" },
        comment: { comment: comment },
      };
      updateRisk(payload, () => {
        callback();
        onComplete();
      });
    };
    const dialogConfig = {
      title: "Archive the Risk?",
      variant: "CAUTION",
      description: "Are you sure you want to archive this Risk?",
      confirm: {
        label: "Archive",
        icon: "CHECK_CONFIRM",
        variant: "CAUTION",
        btnType: "OUTLINED",
        onClick: onConfirm,
      },
      cancel: { btnType: "CONTAINED" },
    };
    return updateState({ dialogConfig });
  };

  const handleUnarchive = () => {
    const onComplete = () => {
      _infoRef.current.refresh();
      updateState({ selectedMenu: null, dialogConfig: null });
    };
    const onConfirm = (_, __, callback) => {
      const isCompleted = isRiskCompleted(
        riskStatusList,
        currentRisk.risk_status,
      );
      const status = isCompleted ? "accepted" : "draft";
      const comment = [
        {
          comment: `The Risk is unarchived by <span class="fw-700">${currentUser.name}</span>.`,
        },
      ];
      updateRisk({ risk: { status: status }, comment: comment }, () => {
        callback();
        onComplete();
      });
    };
    const dialogConfig = {
      title: "Unarchive the Risk?",
      description: "Are you sure you want to unarchive this Risk?",
      confirm: {
        label: "Unarchive",
        icon: "CHECK_CONFIRM",
        onClick: onConfirm,
      },
    };
    return updateState({ dialogConfig });
  };

  const handleShowDetails = () => {
    updateState({ selectedMenu: null, dialogConfig: null });
  };

  const handleShowReport = () => {
    updateState({ selectedMenu: "report" });
  };

  const updateRisk = (payload, iSuccess, iFailure) => {
    const action = riskActions.updateRisk(payload);
    action.onComplete = iSuccess;
    action.onFailure = iFailure;
    dispatch(action);
  };

  const getMenuItems = () => {
    if (isRiskArchive) {
      return MenuItems.filter((item) => item.id !== "archive");
    }
    return MenuItems.filter((item) => item.id !== "unarchive");
  };

  const handleSendForReview = () => {
    const onConfirm = (dialogConfig, values, callback) => {
      const { responder } = values;
      const comment = `The Risk has been sent for review to <span class="fw-700">${responder.name}</span> by <span class="fw-700">${currentUser.name}</span>.`;
      const payload = {
        risk: { reviewer_id: responder.id },
        comment: { comment: comment, is_eval_comment: true },
      };
      updateRisk(payload, callback);
    };
    const dialogConfig = {
      title: "Send for Review",
      showResponder: true,
      width: 472,
      description:
        "This Responder you will choose has to review the Risk Acceptance Record.",
      confirm: { label: "Send for Review", onClick: onConfirm },
    };
    updateState({ dialogConfig });
  };

  const handleMarkAsReviewed = () => {
    const onComplete = (dialogConfig, values, callback) => {
      const comment = `The Risk is marked as reviewed by <span class="fw-700">${currentUser.name}</span>.`;
      const payload = {
        risk: {
          status: "reviewed",
          accepter_id: null,
          reviewer_id: currentUser.id,
          reviewed_date: new Date().toISOString(),
        },
        comment: { comment: comment, is_eval_comment: true },
      };
      updateRisk(payload, callback);
    };
    const dialogConfig = {
      title: "Mark as Review?",
      description: (
        <span>
          Are you sure you want to mark this document as Reviewed?
          <br />
          <br />
          This action cannot be undone.
        </span>
      ),
      confirm: {
        label: "Mark as Reviewed",
        icon: "CHECK_CONFIRM",
        onClick: onComplete,
      },
      cancel: { label: "Not Now", btnType: "TEXT" },
    };
    updateState({ dialogConfig });
  };

  const handleSendForAcceptance = () => {
    const onConfirm = (_, values, callback) => {
      const { responder } = values;
      const comment = `The Risk has been sent for approval to <span class="fw-700">${responder.name}</span> by <span class="fw-700">${currentUser.name}</span>.`;
      const payload = {
        risk: { accepter_id: responder.id },
        comment: { comment: comment, is_eval_comment: true },
      };
      updateRisk(payload, callback);
    };
    const dialogConfig = {
      title: "Send for Acceptance",
      showResponder: true,
      width: 472,
      description:
        "This Responder you will choose has to review the Risk Acceptance Record.",
      confirm: { label: "Send for Acceptance", onClick: onConfirm },
    };
    updateState({ dialogConfig });
  };

  const handleAccept = () => {
    const onConfirm = (_, __, callback) => {
      const comment = [
        {
          comment: `The Risk is marked as accepted by <span class="fw-700">${currentUser.name}</span>.`,
          is_eval_comment: true,
        },
      ];
      const payload = {
        risk: {
          status: "accepted",
          risk_status: "tolerated",
          // risk_status: "accepted", //to trigger email
          accepter_id: currentUser.id,
          accepted_date: new Date().toISOString(),
        },
        comment: comment,
      };
      updateRisk(payload, () => {
        _infoRef.current.refresh();
        callback();
      });
    };
    const dialogConfig = {
      title: "Accept the Risk?",
      description: (
        <span>
          Are you sure you want to Accept this Risk?
          <br />
          <br />
          This action cannot be undone.
        </span>
      ),
      confirm: { label: "Confirm", icon: "CHECK_CONFIRM", onClick: onConfirm },
      cancel: { label: "Not Now", btnType: "TEXT" },
    };
    updateState({ dialogConfig });
  };

  const getOptions = (attribute) => {
    if (attribute === "decision") {
      return decisionList;
    }
    if (attribute === "severity") {
      return severityList;
    }
    if (attribute === "risk_status") {
      return riskStatusList;
    }
    return [];
  };

  const handleDialogClose = () => {
    updateState({ dialogConfig: null });
    _headerRef.current.resetDirtyFields();
  };

  const handleDialogConfirm = useCallback(
    (dialogConfig, values, callback) => {
      const { newValue, attribute } = dialogConfig;
      const { note } = values;
      const label = attribute === "decision" ? "Decision" : "Status";
      const payload = { risk: { [attribute]: newValue.id } };
      if (note) {
        const comment = `
      The Risk ${label} has been changed to <span class="fw-700">${
        newValue.label
      }</span> by <span class="fw-700">${currentUser.name}</span>.<br />
      ${note ? `Note: <br/>${note}` : ""}
      `;
        payload.comment = { comment: comment };
      }
      updateRisk(payload, () => {
        _headerRef.current.updateFormData({ [attribute]: newValue.id });
        _headerRef.current.resetDirtyFields();
        callback();
      });
    },
    [state, currentUser],
  );

  const handleDecisionStatusChange = (attribute, value) => {
    let dialogConfig = {
      attribute: attribute,
      newValue: value,
      confirm: { onClick: handleDialogConfirm },
      cancel: { onClick: handleDialogClose },
    };
    if (attribute === "decision") {
      const decision = decisionList.find(
        (decision) => decision.id.toLowerCase() === value.toLowerCase(),
      );
      if (decision) {
        dialogConfig.newValue = decision;
      }
      if (value.toLowerCase() === "tolerate") {
        dialogConfig.className = "decision-tolerate-popup";
        dialogConfig.title = "Change Risk Decision to Tolerate?";
        dialogConfig.description = (
          <span>
            Tolerating a risk requires a Risk Acceptance Record to be created &
            reviewed. Then it needs to be sent for approval before it becomes a
            record.
            <br />
            <br />
            Risk Acceptance Record will become available in this Risk Record
            AFTER it is approved by the CEO.
            <br />
            <br />
            Confirm change of Decision to "Tolerate".
          </span>
        );
        dialogConfig.variant = "CAUTION";
        dialogConfig.cancel.btnType = "CONTAINED";
        dialogConfig.confirm.label = "Proceed";
        dialogConfig.confirm.icon = "CHECK_CONFIRM";
        dialogConfig.confirm.variant = "CAUTION";
        dialogConfig.confirm.btnType = "OUTLINED";
      } else {
        dialogConfig.title = "Change Risk Treatment Decision?";
        dialogConfig.description = (
          <span>
            This action will change the treatment decision for this Risk. <br />
            <br />
            Provide a note for Risk History.
          </span>
        );
        dialogConfig.showNote = true;
      }
    } else if (attribute === "risk_status") {
      const status = riskStatusList.find(
        (status) => status.id.toLowerCase() === value.toLowerCase(),
      );
      if (status) {
        dialogConfig.newValue = status;
      }
      dialogConfig.title = "Change Risk Mitigation Status?";
      dialogConfig.description = (
        <span>
          This action will update the mitigation status for this Risk. . <br />
          <br />
          Provide a note for Risk History.
        </span>
      );
      dialogConfig.showNote = true;
    } else {
      dialogConfig = null;
    }
    updateState({ dialogConfig: dialogConfig });
  };
  const validate = () => {
    return _headerRef.current.validate() && _infoRef.current.validate();
  };

  const getValues = () => {
    const values = _headerRef.current.getValues();
    const infoValues = _infoRef.current.getValues();
    return { ...values, ...infoValues };
  };

  const getModifiedFields = () => {
    const objectHeaderModifiedFields = _headerRef.current.getDirtyFields();
    const infoModifiedFields = _infoRef.current.getDirtyFields();
    const fields = [...objectHeaderModifiedFields, ...infoModifiedFields];
    fields.sort((fieldA, fieldB) => {
      const aIsText = fieldA.typeRepresentation === "TEXT";
      const bIsText = fieldB.typeRepresentation === "TEXT";
      if (aIsText && !bIsText) return -1;
      if (!aIsText && bIsText) return 1;
      return 0;
    });
    return fields;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }
    const values = getValues();
    let fields = getModifiedFields(values);
    if (fields.length === 0) {
      handleCancel();
      return;
    }
    updateState({ isSubmitting: true });
    const comments = _infoRef.current.getModifiedFieldsComment(fields, values);
    const payload = { risk: values, comment: comments };
    updateRisk(payload, handleCancel, () => {
      updateState({ isSubmitting: false });
    });
  };

  const handleCancel = () => {
    publish("RISK_EDIT_MODE", false);
    publish("NM_OBJECT_HEADER:MODE=VIEW");
    updateState({ isSubmitting: false });
  };

  const handleAutoUpdate = (values) => {
    _headerRef.current.updateFormData(values);
  };

  const getEditPolicy = useCallback(
    (attribute) => {
      if (isRiskArchive) {
        return "READONLY";
      }
      if (
        isRiskAccept &&
        ((attribute === "risk_status" && !isOrganizerUser) ||
          attribute === "decision")
      ) {
        return "READONLY";
      }
      return "ALWAYS";
    },
    [isRiskAccept, isRiskArchive, isOrganizerUser],
  );

  const getActions = useCallback(() => {
    const actions = [];
    let label = "",
      icon = "SEND",
      onClick = () => {};

    if (isRiskTolerated && !isRiskArchive && !isRiskAccept) {
      if (currentRisk.status === "draft") {
        if (currentRisk.reviewer_id === null) {
          label = "Send for Review";
          onClick = handleSendForReview;
        } else if (
          currentRisk.reviewer_id === currentUser.id ||
          isOrganizerUser ||
          isGovernerUser
        ) {
          label = "Mark as Reviewed";
          icon = "CHECK_CONFIRM";
          onClick = handleMarkAsReviewed;
        }
      } else if (currentRisk.status === "reviewed") {
        if (currentRisk.accepter_id === null) {
          label = "Send for Acceptance";
          onClick = handleSendForAcceptance;
        } else if (
          currentRisk.accepter_id === currentUser.id ||
          isOrganizerUser ||
          isGovernerUser
        ) {
          label = "Accept Risk";
          icon = "CHECK_CONFIRM";
          onClick = handleAccept;
        }
      }
    }
    if (label && onClick) {
      actions.push({
        label,
        onClick,
        icon: icon,
        btnType: "CONTAINED",
        classes: { button: "mr-16" },
      });
    }
    return actions;
  }, [currentRisk]);

  return (
    <Col className="w-100 h-100 overflow-hide bg-contrast">
      {!currentRisk && (
        <Col className="w-100 h-100" align="center" justify="center">
          <NMWaitIndicator size="small" />
        </Col>
      )}
      {currentRisk && state.selectedMenu !== "report" && (
        <Fragment>
          <NMObjectHeader
            object={currentRisk}
            classes={{
              root: "no-shadow",
            }}
            objectProps={{
              name: "risk",
              idAttribute: "module_risk_id",
              titleAttribute: "title",
            }}
            ref={_headerRef}
            level={{
              label: "Register",
              onClick: handleGoBack,
            }}
            formData={currentRisk}
            fields={[]}
            menu={{
              headerButton: { btnType: "CONTAINED", icon: "DOT_MENU" },
              items: getMenuItems(),
              onMenuClick: handleMenuClick,
            }}
            isSubmitting={state.isSubmitting}
            actions={getActions()}
            itemsOrder={["priority", "severity", "status", "actions", "menu"]}
            getOptions={getOptions}
            severity={{
              attribute: "severity",
              editPolicy: "READONLY",
              confirmation: true,
              classes: { root: "w-150-px" },
            }}
            status={{
              attribute: "risk_status",
              editPolicy: getEditPolicy("risk_status"),
              confirmation: true,
              customPopup: true,
              classes: { root: "w-210-px mr-12" },
            }}
            priority={{
              attribute: "decision",
              label: "Decision",
              editPolicy: getEditPolicy("decision"),
              confirmation: true,
              customPopup: true,
              classes: {
                root: `mr-12 ${isRiskAccept || isRiskArchive ? "w-175-px" : "w-200-px"}`,
              },
            }}
            onFieldValueChange={handleDecisionStatusChange}
            edit={
              isRiskAccept || isRiskArchive
                ? undefined
                : { onClick: handleEdit }
            }
            save={
              isRiskAccept || isRiskArchive
                ? undefined
                : { onClick: handleSave }
            }
            cancel={
              isRiskAccept || isRiskArchive
                ? undefined
                : { onClick: handleCancel }
            }
          />
          <Col className="w-100 flex-auto overflow-auto pl-20 pr-20">
            <Fragment>
              <InfoSection ref={_infoRef} handleAutoUpdate={handleAutoUpdate} />
              <MitigationSection />
              <RiskAcceptanceSection
                ref={_acceptanceRef}
                onShowReport={handleShowReport}
              />
            </Fragment>
          </Col>
          {state.selectedMenu === "history" && (
            <RiskHistoryPopup onClose={handleShowDetails} />
          )}
          {state.selectedMenu === "reference" && (
            <RiskReferencesPopup onClose={handleShowDetails} />
          )}
          {state.dialogConfig && (
            <RiskConfirmationPopup
              onClose={handleShowDetails}
              dialogConfig={state.dialogConfig}
            />
          )}
        </Fragment>
      )}
      {currentRisk && state.selectedMenu === "report" && (
        <RiskReport onShowDetails={handleShowDetails} />
      )}
    </Col>
  );
};
